import { Inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

// API
import { ApiCore } from '../../config/api';
import jwtDecode from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private url: string = ApiCore.BASE_URL;
  private defaultHeaders = new HttpHeaders()
    .append('Content-Type', 'application/json')
    .append('Prefer', 'count=exact')
    .append('Prefer', 'resolution=merge-duplicates');

  constructor(private httpClient: HttpClient) {}

  /**
   * Get a list of objects/data from the API
   * @param endpoint Object/data name
   * @param queryParams  GET parameters to be included in the query
   */
  public list(endpoint: string, queryParams?: any): Observable<any> {
    let queryParamsString = '';
    if (queryParams) {
      queryParamsString = Object.keys(queryParams)
        .map((key) => key + '=' + queryParams[key])
        .join('&');
      return this.httpClient.get(
        `${this.url}${endpoint}?${queryParamsString}`,
        { headers: this.defaultHeaders }
      );
    } else {
      return this.httpClient.get(`${this.url}${endpoint}`, {
        headers: this.defaultHeaders,
      });
    }
  }

  /**
   * Get single object/data from API
   * @param endpoint Object/data name
   * @param id Object/data identifier
   */
  public get(endpoint: string, id?: string): Observable<any> {
    return this.httpClient.get(`${this.url}${endpoint}`);
  }

  /**
   * Get single object/data from API
   * @param endpoint Object/data name
   * @param id Object/data identifier
   */
  public getById(endpoint: string, id: string): Observable<any> {
    return this.httpClient.get(`${this.url}${endpoint}/${id}`);
  }

  /**
   * Get single object/data from API
   * @param endpoint Object/data name
   * @param id Object/data identifier
   * @param agentid
   */
  public getByIdAndAgentId(
    endpoint: string,
    id: string,
    agentid: string
  ): Observable<any> {
    return this.httpClient.get(`${this.url}${endpoint}/${id}`, {
      params: new HttpParams().set('agentId', agentid),
    });
  }

  /**
   * Create an object/data using API
   * @param endpoint Object/data name
   * @param object Object/data as a JSON {key:value}
   */
  public create(endpoint: string, object: any): Observable<any> {
    return this.httpClient.post(`${this.url}${endpoint}`, object);
  }

  /**
   * Update an object/data using API
   * @param endpoint Object/data name
   * @param id Object/data identifier
   * @param object Object/data as a JSON {key:value}
   */
  public update(endpoint: string, id: string, object: any): Observable<any> {
    return this.httpClient.put(`${this.url}${endpoint}/${id}`, object);
  }

  /**
   * Update an object/data using API
   * @param endpoint Object/data name
   * @param object Object/data as a JSON {key:value}
   */
  public updateDirect(endpoint: string, object: any): Observable<any> {
    return this.httpClient.put(`${this.url}${endpoint}`, object);
  }

  /**
   * Update an object/data using API
   * @param endpoint Object/data name
   * @param object Object/data as a JSON {key:value}
   */
  public updateDirectPost(endpoint: string, object: any): Observable<any> {
    return this.httpClient.post(`${this.url}${endpoint}`, object);
  }

  /**
   * Delete an object/data from API
   * @param endpoint Object/data name
   * @param id Object/data identifier
   */
  public delete(endpoint: string, id: string): Observable<any> {
    return this.httpClient.delete(`${this.url}/${endpoint}/${id}`);
  }

  public GetOperatorConfig(operatorId?: string): Observable<any> {
    let ep = 'operator/operator-config';
    if (operatorId) {
      ep += `?operatorId=${operatorId}`;
    }
    return this.httpClient.get(`${this.url}${ep}`);
  }

  public getCoorporateData(url?: string): Observable<any> {
    let ep = 'operator/coorporate-config';
    if (url) {
      ep += `?url=${url}`;
    }
    return this.httpClient.get(`${this.url}${ep}`);
  }

  /**
   * Catch the error and get the data from error property
   * @param error HttpErrorResponse object
   * @returns JSON object with the error property value
   */
  private errorHandler(error: HttpErrorResponse) {
    return throwError(() => new Error(error.error));
  }

  // Returns an observable
  public upload(endpoint: string, file: any): Observable<any> {
    // Create form data
    const formData = new FormData();
    // Store form name as "file" with file data
    formData.append('file', file, file.name);

    // Make http post request over api
    // with formData as req
    return this.httpClient.post(`${this.url}${endpoint}`, formData);
  }

  // Returns an observable
  public uploadImageProfile(endpoint: string, file: any): Observable<any> {
    // Create form data
    const formData = new FormData();
    // Store form name as "file" with file data
    formData.append('file', file, file.name);

    // Make http post request over api
    // with formData as req
    return this.httpClient.put(`${this.url}${endpoint}`, formData);
  }

  public uploadOperatorLogo(endpoint: string, data: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', data.file, data.file.name);
    formData.append('id', data.id);
    return this.httpClient.put(`${this.url}${endpoint}`, formData);
  }

  /**
   * Get single object/data from API
   * @param endpoint Object/data name
   * @param id Object/data identifier
   */
  public getGameURL(endpoint: string, id: string): Observable<any> {
    return this.httpClient.get(`${this.url}/${endpoint}/${id}`);
  }

  public getOperatorId(session_token: any): Observable<any> {
    const decode: any = jwtDecode(session_token);
    return this.httpClient.get(
      `${this.url}operator/operator-id?id=${
        decode.id
      }&email=${encodeURIComponent(decode.email)}`
    );
  }

  public getWebVersion(): Observable<any> {
    return this.httpClient.get<{ version: string }>(
      `/assets/json/version.json`
    );
  }

  public createSlidder(endpoint: string, data: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', data.web, data.web.name);
    formData.append('file', data.mobile, data.mobile.name);
    formData.append('title', data.title);
    formData.append('slidderStatusCatId', data.status);
    formData.append('position', data.position);
    formData.append('operatorId', localStorage.getItem('o60pf2u4s9i5db') || '');

    return this.httpClient.post(`${this.url}${endpoint}`, formData);
  }

  public deleteSlidder(endpoint: string, id: string): Observable<any> {
    return this.httpClient.delete(`${this.url}${endpoint}/${id}`);
  }

  public updateSlidder(
    endpoint: string,
    data: any,
    id: string
  ): Observable<any> {
    const formData = new FormData();
    if (data.web) {
      formData.append('file', data.web, data.web.name);
    }

    if (data.mobile) {
      formData.append('file', data.mobile, data.mobile.name);
    }

    formData.append('title', data.title);
    formData.append('slidderStatusCatId', data.status);
    formData.append('position', data.position);
    formData.append('operatorId', localStorage.getItem('o60pf2u4s9i5db') || '');

    return this.httpClient.put(`${this.url}${endpoint}/${id}`, formData);
  }
}
